import { useEffect, useRef, useState } from 'preact/hooks';
import { NOTE_MAX_MS, NoteRecorder, type RecordedNote } from './recorder';

const clock = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const icons = {
  trash: 'M5 7h14M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3',
  stop: 'M7 7h10v10H7z',
  play: 'M8 5.5v13l11-6.5-11-6.5z',
  pause: 'M8 5h3v14H8zM13 5h3v14h-3z',
  redo: 'M4 12a8 8 0 1 0 2.5-5.8M4 4v5h5',
  send: 'M5 12l14-7-5 15-2.5-6L5 12z',
};

function Icon({ d, size = 18, fill }: { d: string; size?: number; fill?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={d}
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill={fill ? 'currentColor' : 'none'}
      />
    </svg>
  );
}

/**
 * The voice note bar that replaces the message box: it records straight away, then lets the
 * visitor listen, re-record, discard or send. Stops by itself at one minute.
 */
export function NoteBar({
  onSend,
  onClose,
}: {
  onSend: (note: RecordedNote, previewUrl: string) => void;
  onClose: (error?: string) => void;
}) {
  const recorder = useRef<NoteRecorder | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [phase, setPhase] = useState<'starting' | 'recording' | 'review'>('starting');
  const [elapsed, setElapsed] = useState(0);
  const [note, setNote] = useState<{ rec: RecordedNote; url: string } | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [take, setTake] = useState(0);

  // Record on open, and again after "Re-record".
  useEffect(() => {
    const r = new NoteRecorder();
    recorder.current = r;
    let cancelled = false;
    setPhase('starting');
    r.start()
      .then(() => {
        if (cancelled) return r.cancel();
        setPhase('recording');
      })
      .catch(() => onClose('Please allow microphone access to record a voice note.'));
    return () => {
      cancelled = true;
      r.cancel();
    };
  }, [take]);

  // Recording timer, with the one-minute cut-off.
  useEffect(() => {
    if (phase !== 'recording') return;
    const t = setInterval(() => {
      const ms = recorder.current?.elapsed() ?? 0;
      setElapsed(ms);
      if (ms >= NOTE_MAX_MS) void stop();
    }, 200);
    return () => clearInterval(t);
  }, [phase]);

  // A recording that was never sent is released when the bar closes.
  const unsent = useRef<string | null>(null);
  useEffect(() => () => void (unsent.current && URL.revokeObjectURL(unsent.current)), []);

  async function stop() {
    const r = recorder.current;
    if (!r) return;
    recorder.current = null;
    const rec = await r.stop();
    if (rec.durationMs < 500 || !rec.blob.size) {
      onClose('That was too short. Hold on a moment longer next time.');
      return;
    }
    const url = URL.createObjectURL(rec.blob);
    unsent.current = url;
    setNote({ rec, url });
    setPosition(0);
    setPhase('review');
  }

  function toggle() {
    const el = audio.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  }

  function rerecord() {
    audio.current?.pause();
    if (note) URL.revokeObjectURL(note.url);
    unsent.current = null;
    setNote(null);
    setElapsed(0);
    setTake((n) => n + 1);
  }

  const duration = note?.rec.durationMs ?? elapsed;
  return (
    <div class="vs-note" role="group" aria-label="Voice note">
      <button
        type="button"
        class="vs-note-btn"
        aria-label="Discard voice note"
        onClick={() => {
          recorder.current?.cancel();
          onClose();
        }}
      >
        <Icon d={icons.trash} />
      </button>

      {phase === 'review' && note ? (
        <>
          <button
            type="button"
            class="vs-note-btn vs-note-play"
            aria-label={playing ? 'Pause' : 'Listen'}
            onClick={toggle}
          >
            <Icon d={playing ? icons.pause : icons.play} fill />
          </button>
          <div class="vs-note-track" aria-hidden="true">
            <span style={{ width: `${Math.min(100, (position / (duration || 1)) * 100)}%` }} />
          </div>
          <span class="vs-note-time">{clock(playing || position ? position : duration)}</span>
          <audio
            ref={audio}
            src={note.url}
            preload="auto"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => {
              setPlaying(false);
              setPosition(0);
            }}
            onTimeUpdate={(e) => setPosition((e.target as HTMLAudioElement).currentTime * 1000)}
          />
          <button type="button" class="vs-note-btn" aria-label="Re-record" onClick={rerecord}>
            <Icon d={icons.redo} />
          </button>
          <button
            type="button"
            class="vs-send is-ready"
            aria-label="Send voice note"
            onClick={() => {
              audio.current?.pause();
              const sent = note;
              // The chat bubble keeps playing the preview, so it is not released here.
              unsent.current = null;
              setNote(null);
              onSend(sent.rec, sent.url);
            }}
          >
            <Icon d={icons.send} />
          </button>
        </>
      ) : (
        <>
          <span class="vs-note-rec" aria-hidden="true" />
          <span class="vs-note-label" aria-live="polite">
            {phase === 'starting' ? 'Starting…' : 'Recording'}
          </span>
          <span class="vs-note-time">
            {clock(elapsed)} / {clock(NOTE_MAX_MS)}
          </span>
          <button
            type="button"
            class="vs-send is-ready vs-note-stop"
            aria-label="Stop recording"
            disabled={phase !== 'recording'}
            onClick={() => void stop()}
          >
            <Icon d={icons.stop} fill />
          </button>
        </>
      )}
    </div>
  );
}

/** Plays a sent voice note: the local preview, or the stored recording fetched on first play. */
export function NotePlayer({
  durationMs,
  src,
  load,
}: {
  durationMs: number;
  src?: string;
  load: () => Promise<string>;
}) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [url, setUrl] = useState<string | undefined>(src);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [failed, setFailed] = useState(false);

  async function toggle() {
    let source = url;
    if (!source) {
      try {
        source = await load();
        setUrl(source);
      } catch {
        setFailed(true);
        return;
      }
    }
    const el = audio.current;
    if (!el) return;
    if (el.src !== source) el.src = source;
    if (el.paused) void el.play();
    else el.pause();
  }

  return (
    <div class="vs-note-player">
      <button
        type="button"
        class="vs-note-btn vs-note-play"
        aria-label={playing ? 'Pause voice note' : 'Play voice note'}
        onClick={() => void toggle()}
      >
        <Icon d={playing ? icons.pause : icons.play} size={16} fill />
      </button>
      <div class="vs-note-track" aria-hidden="true">
        <span style={{ width: `${Math.min(100, (position / (durationMs || 1)) * 100)}%` }} />
      </div>
      <span class="vs-note-time">
        {failed ? 'Unavailable' : clock(playing || position ? position : durationMs)}
      </span>
      <audio
        ref={audio}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPosition(0);
        }}
        onTimeUpdate={(e) => setPosition((e.target as HTMLAudioElement).currentTime * 1000)}
      />
    </div>
  );
}
